import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd, lockScroll, unlockScroll } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * Custom events for Microsoft Clarity (heatmaps and filters by event). Clarity may not be loaded
 * yet or may be blocked by the visitor's consent choice; nothing in the shop depends on this.
 * @param {string} name
 */
const track = (name) => {
  try {
    if (typeof window.clarity === 'function') window.clarity('event', name);
  } catch {
    // measurement must never break the page
  }
};

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** The bar counts as "shown" once per page view, however often it slides in and out */
  #shownTracked = false;

  /** @type {HTMLDialogElement | null} */
  #sheet = null;

  /** @type {number | undefined} */
  #sheetBusyTimeout;

  /** True from tapping a size until the product page has finished switching to it */
  #selectionPending = false;

  /** @type {number | undefined} */
  #addingTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    // A tap on the page size grid counts as a choice even when it is the pre-selected size, which
    // changes no variant and so sends no productSelect
    target?.addEventListener('pk:size-picked', this.#markSizeChosen, { signal });

    // Button above the size grid (phones only): opens the same size sheet as the bar, both as
    // "Vyber velikost" and, once a size is chosen, as "Velikost EU 42.5 · Změnit"
    document.addEventListener(
      'click',
      (event) => {
        const control = event.target instanceof Element ? event.target.closest('[data-quick-cta]') : null;
        if (!control || !event.isTrusted || !this.isConnected) return;
        if (!window.matchMedia('(max-width: 749px)').matches || this.dataset.adding === 'true') return;
        const quick = control.closest('[data-quick]');
        // One size in stock: "Jiná velikost?" goes straight to similar models in the visitor's size
        // (snippets/pk-size-alts.liquid); the size sheet would only offer the same single size again
        if (quick?.hasAttribute('data-single') && document.documentElement.dataset.pkSizeAlts === 'ready') {
          track('pk_quick_other_size');
          document.dispatchEvent(new CustomEvent('pk-size-alts:open', { detail: {} }));
          return;
        }
        track(quick?.hasAttribute('data-chosen') ? 'pk_quick_change' : 'pk_quick_size_tap');
        this.#openSheet();
      },
      { signal }
    );

    // The buy buttons of the product page itself ("Přidat do košíku", "Koupit teď"). No size is
    // chosen for the visitor, so until one is chosen they ask for it instead of buying the default.
    // (The bar's own click on the form button is a script click and not trusted; the bar's button
    // is excluded as well.)
    document.addEventListener(
      'click',
      (event) => {
        if (!event.isTrusted || !(event.target instanceof Element)) return;
        const button = event.target.closest('[ref="addToCartButton"], .accelerated-checkout-block');
        if (!button || button.closest('sticky-add-to-cart') || !button.closest('.shopify-section')?.contains(this)) return;

        if (this.dataset.multiVariant === 'true' && this.dataset.sizeChosen !== 'true') {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.#askForSize();
          return;
        }
        track('pk_page_add');
      },
      { signal, capture: true }
    );

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
    this.#setupSizeSheet(signal);

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
    // chat-drawer.liquid adds <shopify-chat> only at the visitor's first interaction
    document.addEventListener(
      'pk:chat-mounted',
      () => customElements.whenDefined('shopify-chat').then(() => {
        if (signal.aborted) return;
        if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
      }),
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    clearTimeout(this.#sheetBusyTimeout);
    clearTimeout(this.#addingTimeout);
    this.#sheet?.remove();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        // The observer already measured it; reading the rect again forced a layout on page load
        const rect = entry.boundingClientRect;
        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatActive()) return;
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        // The mobile bar hides at the footer too (CSS reads this flag)
        this.dataset.atFooter = String(entry.isIntersecting);

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            if (!this.#isChatActive()) {
              this.#showStickyBar();
            }
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);

    // An IntersectionObserver only reports when the block crosses the edge of the screen. A fast
    // fling (or a jump with a link or the scrollbar) can pass it between two frames, and the bar
    // would never appear. This cheap check on scroll (one rect read per frame) covers that case.
    let scrollQueued = false;
    const syncWithScroll = () => {
      scrollQueued = false;
      if (this.#hiddenByBottom) return;
      const scrolledPast = buyButtonsBlock.getBoundingClientRect().bottom < 0;
      if (scrolledPast && !this.#isStuck && !this.#isChatActive()) this.#showStickyBar();
      else if (!scrolledPast && this.#isStuck) this.#hideStickyBar();
    };
    window.addEventListener(
      'scroll',
      () => {
        if (scrollQueued) return;
        scrollQueued = true;
        requestAnimationFrame(syncWithScroll);
      },
      { passive: true, signal: this.#abortController.signal }
    );
    syncWithScroll();
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Bar button: on a phone, a size nobody has chosen yet is only the default, so ask for one in the
   * bottom sheet instead of adding it silently. In every other case it adds to the cart.
   */
  handleCtaClick = () => {
    if (this.dataset.adding === 'true') return;
    if (this.#needsSize()) {
      track('pk_bar_cta_opens_sheet');
      this.#openSheet();
      return;
    }
    track('pk_bar_add');
    this.#add();
  };

  /**
   * Someone tried to buy without choosing a size. On a phone the size sheet opens; on a larger
   * screen the size grid is scrolled into view and a short message appears under it.
   */
  #askForSize() {
    track('pk_buy_without_size');
    const warning = this.#findMainTrigger('[data-size-warning]');
    warning?.removeAttribute('hidden');

    if (window.matchMedia('(max-width: 749px)').matches) {
      this.#openSheet();
      return;
    }

    const grid = this.#findMainTrigger('[data-variant-container]');
    if (!grid) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    grid.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    grid.classList.remove('is-attention');
    void grid.offsetWidth;
    grid.classList.add('is-attention');
  }

  /**
   * Picked size in the bar: on a phone it opens the size sheet to change it (the sheet only exists
   * on phones, so on larger screens the text stays plain).
   */
  handleSizeClick = () => {
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    if (this.dataset.adding === 'true') return;
    track('pk_bar_size_tap');
    this.#openSheet();
  };

  /**
   * Adds the selected size through the real product form (so the cart drawer, cart count and error
   * handling are the theme's own) and shows a short "adding" state that always clears again: on the
   * cart response, on an error, or after a timeout. Buttons are never left disabled.
   */
  async #add() {
    if (this.dataset.adding === 'true') return;
    this.#setAdding(true);

    // A size that was just tapped must have reached the form, and the form button must be usable
    const ready = await this.#waitFor(() => !this.#selectionPending && this.#formButtonReady(), 3000);
    if (!ready || !this.#targetAddToCartButton) {
      this.#setAdding(false);
      return;
    }

    this.handleAddToCartClick();
    // Fallback only: normally cleared by the cart events below
    this.#addingTimeout = window.setTimeout(() => this.#setAdding(false), 8000);
  }

  /** @param {boolean} adding */
  #setAdding(adding) {
    clearTimeout(this.#addingTimeout);
    if (adding) this.dataset.adding = 'true';
    else delete this.dataset.adding;
    this.refs.addToCartButton?.setAttribute('aria-busy', String(adding));
    const sheetCta = this.#sheet?.querySelector('[data-size-sheet-add]');
    if (adding) sheetCta?.setAttribute('aria-busy', 'true');
    else sheetCta?.removeAttribute('aria-busy');
  }

  /** @returns {boolean} */
  #formButtonReady() {
    const form = this.#getProductForm();
    if (form) this.#targetAddToCartButton = form.querySelector('[ref="addToCartButton"]');
    return Boolean(this.#targetAddToCartButton) && !this.#targetAddToCartButton?.disabled;
  }

  /**
   * @param {() => boolean} condition
   * @param {number} timeout
   * @returns {Promise<boolean>}
   */
  async #waitFor(condition, timeout) {
    const start = Date.now();
    while (!condition()) {
      if (Date.now() - start > timeout) return false;
      await new Promise((resolve) => setTimeout(resolve, 80));
    }
    return true;
  }

  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    // Note: previously read `getComputedStyle(this.refs.productImage)` into an unused
    // variable here. That forces the browser to synchronously flush pending style/layout
    // work queued up by the dataset writes and the .click() call just above (a classic
    // forced-reflow pattern) — for a value nothing downstream ever read. Removing it drops
    // that synchronous layout cost from the add-to-cart click path, one of the most
    // frequently exercised interactions on the site, without changing any behavior.
    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    this.#markSizeChosen();
    // A buy tap right after choosing a size waits until the form has switched to it (#add), whichever
    // control the size was chosen with
    if (event.promise) this.#selectionPending = true;
    event.promise
      ?.finally(() => {
        this.#selectionPending = false;
      })
      .catch(() => {});

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    if (event instanceof CartErrorEvent) this.#setAdding(false);
    else if ('promise' in event && event.promise instanceof Promise) {
      event.promise.then(() => this.#setAdding(false), () => this.#setAdding(false));
    }

    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    if (!this.#shownTracked) {
      this.#shownTracked = true;
      track('pk_bar_shown');
    }
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Size sheet (phones)
  /**
   * @param {AbortSignal} signal
   */
  #setupSizeSheet(signal) {
    // A size in the URL (?variant=) is a deliberate choice, e.g. from a link or an ad
    if (new URLSearchParams(window.location.search).has('variant')) this.dataset.sizeChosen = 'true';
    this.#signal = signal;
  }

  /** @type {AbortSignal | undefined} */
  #signal;

  /**
   * The sheet lives in <body>, cloned once from the template: the product page re-renders this
   * section after every size change, which would reset a sheet kept inside it.
   * @returns {HTMLDialogElement | null}
   */
  #ensureSheet() {
    if (this.#sheet?.isConnected) return this.#sheet;

    const template = this.querySelector('template[data-size-sheet-template]');
    const sheet = /** @type {HTMLDialogElement | null} */ (
      template instanceof HTMLTemplateElement ? template.content.firstElementChild?.cloneNode(true) : null
    );
    if (!sheet) return null;

    sheet.addEventListener(
      'click',
      (event) => {
        const target = /** @type {Element} */ (event.target);
        if (target === sheet || target.closest('[data-size-sheet-close]')) {
          sheet.close();
          return;
        }

        const size = /** @type {HTMLElement | null} */ (target.closest('.size-sheet__size'));
        if (size) {
          this.#pickSize(size);
          return;
        }

        // "Chybí tvoje velikost?" does what the same tile under the page grid does
        if (target.closest('[data-size-sheet-missing]')) {
          track('pk_sheet_missing');
          sheet.close();
          this.#findMainTrigger('[data-alert-tile]')?.click();
          return;
        }

        if (target.closest('[data-size-sheet-chart]')) {
          event.preventDefault();
          sheet.close();
          this.#findMainTrigger('[data-size-chart-trigger]')?.click();
          return;
        }

        if (target.closest('[data-size-sheet-add]')) {
          if (this.dataset.adding === 'true') return;
          track('pk_sheet_add');
          sheet.close();
          this.#add();
        }
      },
      { signal: this.#signal }
    );

    // The page behind the sheet must not move: on iPhone a drag on the sheet (or its backdrop) scrolled
    // the product page underneath (Clarity 2026-10-30). Shared owner lock, so other dialogs keep theirs.
    sheet.addEventListener('close', () => unlockScroll(sheet), { signal: this.#signal });

    document.body.appendChild(sheet);
    this.#sheet = sheet;
    return sheet;
  }

  #markSizeChosen = () => {
    this.dataset.sizeChosen = 'true';
    this.#findMainTrigger('[data-size-warning]')?.setAttribute('hidden', '');
  };

  /** @returns {boolean} */
  #needsSize() {
    return (
      this.dataset.multiVariant === 'true' &&
      this.dataset.sizeChosen !== 'true' &&
      window.matchMedia('(max-width: 749px)').matches
    );
  }

  /**
   * @param {string} selector
   * @returns {HTMLElement | null}
   */
  #findMainTrigger(selector) {
    const scope = this.closest('.shopify-section') ?? document;
    return /** @type {HTMLElement | null} */ (scope.querySelector(selector));
  }

  #openSheet() {
    const sheet = this.#ensureSheet();
    if (!sheet || sheet.open) return;

    // Until a size has been chosen nothing in the sheet is pre-selected
    const chosen = this.dataset.sizeChosen === 'true';
    const currentId = /** @type {HTMLElement | null} */ (
      this.#findMainTrigger('[data-variant-container] .selected')
    )?.dataset.variantId;
    let selected = null;
    for (const size of sheet.querySelectorAll('.size-sheet__size')) {
      const isSelected = chosen && /** @type {HTMLElement} */ (size).dataset.variantId === currentId;
      if (isSelected) selected = size;
      this.#markSize(/** @type {HTMLElement} */ (size), isSelected);
      this.#syncSizeMarks(/** @type {HTMLElement} */ (size));
    }
    this.#syncSheetExtras();
    this.#syncSheetCta(/** @type {HTMLElement | null} */ (selected));
    this.#syncSheetDelivery();
    sheet.querySelector('[data-size-sheet-chart]')?.toggleAttribute(
      'hidden',
      !this.#findMainTrigger('[data-size-chart-trigger]')
    );

    sheet.showModal();
    lockScroll(sheet);
  }

  /**
   * Chooses a size by clicking the matching button of the real size picker, so the variant,
   * price, delivery note and product form are updated by the code that already owns them.
   * @param {HTMLElement} size
   */
  #pickSize(size) {
    const sheet = this.#sheet;
    if (!sheet) return;

    track('pk_sheet_size_pick');
    const variantId = size.dataset.variantId;
    const mainButton = /** @type {HTMLElement | null} */ (
      this.#findMainTrigger(`[data-variant-container] [data-variant-id="${variantId}"]`)
    );

    if (size.dataset.soldOut) {
      // The picker opens similar models in that size, or its "notify me" dialog; two dialogs must not stack
      if (mainButton?.closest('.is-alerts-active') || document.documentElement.dataset.pkSizeAlts === 'ready') {
        sheet.close();
        mainButton.click();
      }
      return;
    }

    for (const other of sheet.querySelectorAll('.size-sheet__size')) {
      this.#markSize(/** @type {HTMLElement} */ (other), other === size);
    }
    this.#syncSheetCta(size);
    // Tapping the size that is already selected changes nothing, so there is nothing to wait for
    if (mainButton && !mainButton.classList.contains('selected')) {
      this.#selectionPending = true;
      // Never wait forever if the section request fails
      clearTimeout(this.#sheetBusyTimeout);
      this.#sheetBusyTimeout = window.setTimeout(() => {
        this.#selectionPending = false;
      }, 2500);
    } else {
      this.#markSizeChosen();
    }
    mainButton?.click();
    // The click above updates the delivery strip under the size grid at once
    this.#syncSheetDelivery();
  }

  /**
   * Shows the delivery time of the chosen size inside the sheet, taken from the strip under the real
   * size grid (it already knows own stock vs external warehouse). Hidden when that strip is hidden,
   * so the sheet never promises a delivery time the page does not.
   */
  #syncSheetDelivery() {
    const box = this.#sheet?.querySelector('[data-size-sheet-delivery]');
    if (!(box instanceof HTMLElement)) return;
    const status = this.#findMainTrigger('[data-status]');
    const text = status?.querySelector('[data-status-sub]')?.textContent?.trim();
    const show = Boolean(status && !status.hidden && text);
    box.hidden = !show;
    if (!show || !status) return;
    box.classList.toggle('is-ext', status.classList.contains('is-ext'));
    const label = box.querySelector('[data-size-sheet-delivery-text]');
    if (label) label.textContent = text ?? '';
  }

  /**
   * The sheet tiles show what the page grid shows for the same size: "Poslední" (one pair left),
   * the bolt (own stock) or the van (external warehouse), and "Hlídat" on a sold-out size when
   * alerts are on. Read from the grid, which works it out on the server, so they never disagree.
   * @param {HTMLElement} size
   */
  #syncSizeMarks(size) {
    const main = /** @type {HTMLElement | null} */ (
      this.#findMainTrigger(`[data-variant-container] [data-variant-id="${size.dataset.variantId}"]`)
    );
    if (!main) return;
    size.dataset.kind = main.dataset.kind ?? 'none';
    size.toggleAttribute('data-last', main.dataset.last === 'true');
    if (size.dataset.soldOut) {
      const label = main.querySelector('[class*="ai-variant-button-price"]')?.textContent?.trim();
      const price = size.querySelector('.size-sheet__size-price');
      if (label && price) price.textContent = label;
    }
  }

  /** The "Chybí tvoje velikost?" tile and the symbol legend, only when the page grid has them */
  #syncSheetExtras() {
    const sheet = this.#sheet;
    if (!sheet) return;
    const missing = sheet.querySelector('[data-size-sheet-missing]');
    const mainTile = this.#findMainTrigger('[data-alert-tile]');
    if (missing instanceof HTMLElement) missing.hidden = !mainTile || mainTile.hidden;

    const legend = sheet.querySelector('[data-size-sheet-legend]');
    const mainLegend = this.#findMainTrigger('[data-legend]');
    if (legend instanceof HTMLElement) {
      legend.innerHTML = mainLegend ? mainLegend.innerHTML : '';
      legend.hidden = !mainLegend;
    }
  }

  /**
   * @param {HTMLElement} size
   * @param {boolean} selected
   */
  #markSize(size, selected) {
    size.classList.toggle('is-selected', selected);
    size.setAttribute('aria-pressed', String(selected));
  }

  /** @param {HTMLElement | null} size */
  #syncSheetCta(size) {
    const cta = this.#sheet?.querySelector('[data-size-sheet-add]');
    if (!(cta instanceof HTMLButtonElement)) return;
    cta.disabled = !size;
    const price = size?.querySelector('.size-sheet__size-price')?.textContent;
    const priceEl = cta.querySelector('[data-size-sheet-price]');
    if (priceEl) priceEl.textContent = size && price ? price : '';
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is added by chat-drawer.liquid at the first interaction, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    // On a phone the bar is the buy button: the chat bubble sits above it instead of replacing it
    if (window.matchMedia('(max-width: 749px)').matches) return false;
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
